/**
 * Hero Carousel — progressive enhancement over a native CSS scroll-snap track.
 * Swipe/scroll works with zero JS; this adds arrows, dots, autoplay, and keyboard nav.
 */
(function () {
  'use strict';

  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hero-carousel-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-carousel-slide]'));
      this.prevBtn = this.querySelector('[data-hero-carousel-prev]');
      this.nextBtn = this.querySelector('[data-hero-carousel-next]');
      this.dots = Array.from(this.querySelectorAll('[data-hero-carousel-dot]'));
      this.playPauseBtn = this.querySelector('[data-hero-carousel-playpause]');

      if (!this.track || this.slides.length === 0) return;

      this.loop = this.dataset.loop === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.activeIndex = 0;
      this.autoplayTimer = null;
      this.resizeTimer = null;
      this.userPaused = false;

      this._onPrev = this.goPrev.bind(this);
      this._onNext = this.goNext.bind(this);
      this._onPlayPause = this.togglePlayPause.bind(this);
      this._onKeydown = this.handleKeydown.bind(this);
      this._onResize = this.handleResize.bind(this);
      this._onVisibility = this.handleVisibilityChange.bind(this);
      this._pauseAutoplay = () => this.pauseAutoplay();
      this._resumeAutoplay = () => this.scheduleAutoplay();

      this.setupObserver();
      this.bindEvents();
      this.updateSlideVisibility();

      if (this.autoplayEnabled && !this.reducedMotion) this.scheduleAutoplay();

      // Theme editor: bring a slide into view when its block is selected,
      // so it isn't left inert/hidden while the merchant edits it.
      this._onBlockSelect = (event) => {
        const slide = event.target.closest('[data-hero-carousel-slide]');
        const index = slide ? this.slides.indexOf(slide) : -1;
        if (index !== -1) this.goTo(index);
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.pauseAutoplay();
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('visibilitychange', this._onVisibility);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
    }

    setupObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = this.slides.indexOf(entry.target);
              if (index !== -1) this.setActive(index);
            }
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    bindEvents() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      if (this.playPauseBtn) this.playPauseBtn.addEventListener('click', this._onPlayPause);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goTo(index);
          this.scheduleAutoplay();
        });
      });

      this.track.addEventListener('keydown', this._onKeydown);

      this.addEventListener('mouseenter', this._pauseAutoplay);
      this.addEventListener('mouseleave', this._resumeAutoplay);
      this.addEventListener('focusin', this._pauseAutoplay);
      this.addEventListener('focusout', this._resumeAutoplay);
      this.track.addEventListener('touchstart', this._pauseAutoplay, { passive: true });
      this.track.addEventListener('touchend', this._resumeAutoplay, { passive: true });

      document.addEventListener('visibilitychange', this._onVisibility);
      window.addEventListener('resize', this._onResize);
    }

    setActive(index) {
      this.activeIndex = index;
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      this.updateSlideVisibility();
    }

    // Keep only the current slide in the tab order and the accessibility tree.
    // `inert` also blocks pointer/focus; `aria-hidden` is a belt-and-braces for
    // older assistive tech. Applied by JS only, so a no-JS page stays fully
    // accessible with every slide reachable.
    updateSlideVisibility() {
      this.slides.forEach((slide, i) => {
        const inactive = i !== this.activeIndex;
        slide.inert = inactive;
        slide.setAttribute('aria-hidden', inactive ? 'true' : 'false');
      });
    }

    goTo(index, behavior) {
      if (this.slides.length === 0) return;
      let target = index;
      if (this.loop) {
        target = (index + this.slides.length) % this.slides.length;
      } else {
        target = Math.max(0, Math.min(index, this.slides.length - 1));
      }
      const left = target * this.track.clientWidth;
      this.track.scrollTo({
        left,
        behavior: this.reducedMotion ? 'auto' : behavior || 'smooth',
      });
      this.setActive(target);
    }

    goPrev() {
      this.goTo(this.activeIndex - 1);
      this.scheduleAutoplay();
    }

    goNext() {
      this.goTo(this.activeIndex + 1);
      this.scheduleAutoplay();
    }

    togglePlayPause() {
      this.userPaused = !this.userPaused;
      if (this.playPauseBtn) {
        this.playPauseBtn.setAttribute('aria-pressed', String(this.userPaused));
        this.playPauseBtn.setAttribute('aria-label', this.userPaused ? 'Play slideshow' : 'Pause slideshow');
      }
      if (this.userPaused) {
        this.pauseAutoplay();
      } else {
        this.scheduleAutoplay();
      }
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goNext();
      }
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.goTo(this.activeIndex, 'auto');
      }, 150);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay();
      } else {
        this.scheduleAutoplay();
      }
    }

    scheduleAutoplay() {
      this.pauseAutoplay();
      if (!this.autoplayEnabled || this.reducedMotion || document.hidden || this.userPaused) return;
      this.autoplayTimer = window.setTimeout(() => {
        this.goTo(this.activeIndex + 1);
        this.scheduleAutoplay();
      }, this.autoplaySpeed);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  if (!window.customElements.get('hero-carousel')) {
    window.customElements.define('hero-carousel', HeroCarousel);
  }
})();